import React from 'react';
import { useQuery } from '@apollo/client/react';
import GET_DIDS_FOR_CAMPAIGN_Q from '../gql/getDidsForCampaign.gql';

import { Table } from '@repo/ui/Table';
import { PAGE_SCHEMA, Pagination, decodePaging } from '@repo/ui/Pagination';
import type { SelectInputOptions } from '@repo/types/Types';
import { z } from 'zod';
import { getNonEmpty } from '@repo/util/nonEmpty';
import { useZodQuery } from '@repo/hooks/useZodQuery';
import { coerce } from '@repo/util/coerceForTable';
import { toQueryList } from '@repo/util/toQueryList';
import { combineDateRange } from '@repo/util/combineDateRange';
import { coerceValue } from '@repo/util/coerceValueForQuery';
import { Form } from '@repo/ui/Form';
import DidsForCampaignTableFilter from './DidsForCampaignTableFilter';

import { POLICIES } from '@/lib/Authorization';
import { useUser } from '@repo/hooks/useUser';
import { Link } from 'react-router';

const TYPES: SelectInputOptions = [
  { label: '10DLC', value: 'TENDLC' },
  { label: 'Toll Free', value: 'TOLLFREE' },
  { label: 'Short Code', value: 'SHORTCODE' },
] as const;

const DIDS_FOR_CAMPAIGN_QUERY_SCHEMA = PAGE_SCHEMA.extend({
  did_id: z.string().ulid().or(z.literal('')).default(''),
  number: z.string().default(''),
  type: z
    .enum(getNonEmpty(TYPES.map((dir) => dir.value)))
    .default('')
    .or(z.literal(''))
    .default(''),

  start: z.coerce.date().optional(),
  end: z.coerce.date().optional(),
});

const PAGE_SIZE = 10;

export interface DidsForCampaignTableProps {
  accountId?: string;
  cspCampaignId?: string;
}

const DidsForCampaignTable = ({ accountId, cspCampaignId }: DidsForCampaignTableProps) => {
  const user = useUser();

  const filter = useZodQuery({
    schema: DIDS_FOR_CAMPAIGN_QUERY_SCHEMA,
    ignoreList: ['page'],
    coerce,
  });

  const { page, ...queryFields } = filter.queryValues;
  const queryList = [
    ...toQueryList(
      {
        ...queryFields,
      },
      coerceValue,
      ['number']
    ),
  ];

  const { loading, error, data } = useQuery(GET_DIDS_FOR_CAMPAIGN_Q, {
    variables: {
      ...decodePaging(page, PAGE_SIZE),
      queryList,
      id: cspCampaignId,
    },
    notifyOnNetworkStatusChange: true,
  });

  if (error) {
    console.error(error);
  }

  const { edges: didNodes, pageInfo } = data?.didsForCspCampaign ?? {};

  return (
    <div>
      <Form {...filter} variant="horizontal" className={'mb-6'}>
        <DidsForCampaignTableFilter
          TYPES={TYPES}
          queryList={queryList}
          hasContent={loading || (didNodes?.length ?? 0) > 0}
        />
      </Form>
      <Table
        loading={loading}
        pageSize={PAGE_SIZE}
        data={didNodes?.map((did) => did.node) ?? []}
        dataType="numbers"
        columns={[
          {
            title: 'Number',
            Cell({ entry: { number, id } }) {
              return POLICIES['dids:show'](user) ? (
                <Link to={`/a/${accountId}/numbers/${id}`} className="underline">
                  {number}
                </Link>
              ) : (
                <span> {number}</span>
              );
            },
          },
          {
            title: 'Type',
            Cell({ entry: { type } }) {
              if (type === 'TENDLC') return <p>10DLC</p>;
              return <p>{type}</p>;
            },
          },
          {
            title: 'Capabilities',
            Cell({ entry: { smsEnabled, mmsEnabled } }) {
              return (
                <div className="flex items-center justify-between gap-10 w-min">
                  <span
                    className={`material-symbols-outlined ${
                      smsEnabled
                        ? 'dark:text-blue-100 text-grey-1000'
                        : 'dark:text-grey-600 text-grey-500'
                    }`}
                  >
                    sms
                  </span>
                  <span
                    className={`material-symbols-outlined ${
                      mmsEnabled
                        ? 'dark:text-blue-100 text-grey-1000'
                        : 'dark:text-grey-600 text-grey-500'
                    }`}
                  >
                    mms
                  </span>
                </div>
              );
            },
          },
        ]}
        error={!!error}
      />
      {!error ? <Pagination {...{ pageInfo, filter }} /> : null}
    </div>
  );
};

export default DidsForCampaignTable;
